import React from 'react';
import type { Candidate, Experience } from '@axiom/validation';
import {
  Button,
  Checkbox,
  CondensedLarge,
  CondensedSmall,
  DateInput,
  Dropdown,
  ExclamationMarkCircleIcon,
  Form,
  FormLabel,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Input,
  Layout,
  Location,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ReadonlyTextarea,
  TaxonomyUtil,
  useApi,
} from '@axiom/ui';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateExperiencesApi } from '../../api/candidate-experiences-api';
import { CandidateIndustriesApi } from '../../api/candidate-industries-api';
import { ExperiencesApi } from '../../api/experiences-api';

import {
  CandidateExperienceFormAxiomSchema,
  CandidateExperienceFormNonAxiomSchema,
  type CandidateExperienceFormData,
} from './candidate-experience-form-schema';

export type CandidateExperienceFormProps = {
  candidateId: Candidate['id'];
  experience: Partial<Experience>;
  onClose: () => void;
};

export const CandidateExperienceForm = ({
  candidateId,
  experience,
  onClose,
}: CandidateExperienceFormProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const industryOptions = TaxonomyUtil.getIndustryOptions(taxonomy.industries);

  const isCurrentlyWorkingHere = experience.endDate === null;
  const initialValues = {
    ...experience,
    description: experience.description || '',
    currentlyWorkHere: isCurrentlyWorkingHere,
    endDate: isCurrentlyWorkingHere ? undefined : experience.endDate,
    location: experience.locationPlaceId
      ? {
          locationAddressComponents: experience.locationAddressComponents,
          locationLatitude: experience.locationLatitude,
          locationLongitude: experience.locationLongitude,
          locationName: experience.locationName,
          locationPlaceId: experience.locationPlaceId,
        }
      : null,
  } as Partial<CandidateExperienceFormData>;

  const handleSubmit = async (
    changedData: Partial<CandidateExperienceFormData>
  ) => {
    const payload: Record<string, unknown> = { ...changedData };

    if (payload.externalOpportunityName === null) {
      payload.externalOpportunityName = '';
    }

    if (payload.description === null) {
      payload.description = '';
    }

    if (payload.currentlyWorkHere === true) {
      payload.endDate = null;
    }

    if (payload.location) {
      const location =
        payload.location as CandidateExperienceFormData['location'];
      payload.locationAddressComponents = location?.locationAddressComponents;
      payload.locationName = location?.locationName;
      payload.locationPlaceId = location?.locationPlaceId;
      payload.location = undefined;
    }

    if (experience.isAxiom && payload.industryValue) {
      payload.industryIsOverridden = true;
    }

    await (experience.id
      ? ExperiencesApi.updateExperience(experience.id, payload)
      : CandidateExperiencesApi.createExperience(
          candidateId,
          payload as Parameters<
            typeof CandidateExperiencesApi.createExperience
          >[1]
        ));

    await CandidateExperiencesApi.refreshExperiences(candidateId);
    await CandidateIndustriesApi.refreshIndustries(candidateId);

    onClose();
  };

  const isAxiom = experience.isAxiom || false;
  const schema = isAxiom
    ? CandidateExperienceFormAxiomSchema
    : CandidateExperienceFormNonAxiomSchema;

  return (
    <Form
      name="candidate-experience-form"
      schema={schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ fireSubmit, values, setValues }) => {
        const id = 'id' in values ? values.id : undefined;
        const description = 'description' in values ? values.description : null;
        const currentlyWorkHere =
          'currentlyWorkHere' in values && values.currentlyWorkHere;

        return (
          <Modal size="large" name={id ? 'EDITEXPERIENCE' : 'ADDEXPERIENCE'}>
            <ModalHeader onClose={onClose}>
              {isAxiom
                ? 'Edit Axiom Experience'
                : `${id ? 'Edit' : 'Add'} Other Experience`}
            </ModalHeader>
            <ModalSection>
              <Grid>
                {isAxiom ? (
                  <GridRow gutterBottom="16px">
                    <GridColumn width="12">
                      <Layout position="middle" horizontalGutter="8px">
                        <ExclamationMarkCircleIcon />
                        <CondensedLarge>
                          Some Axiom experience fields must be updated directly
                          in Salesforce.
                        </CondensedLarge>
                      </Layout>
                    </GridColumn>
                  </GridRow>
                ) : null}
                <GridRow gutterBottom="16px">
                  <GridColumn width="6">
                    {isAxiom ? (
                      <Input
                        name="opportunity.jobName"
                        label="OPPORTUNITY"
                        disabled
                      />
                    ) : (
                      <Input name="externalOpportunityName" label="JOB TITLE" />
                    )}
                  </GridColumn>

                  <GridColumn width="6">
                    <Input name="client" label="COMPANY" disabled={isAxiom} />
                    <Gutter top="8px">
                      <Checkbox
                        name="isConfidential"
                        displayValue="Confidential Company"
                      />
                    </Gutter>
                  </GridColumn>
                </GridRow>

                {isAxiom ? (
                  <>
                    <GridRow gutterBottom="16px">
                      <GridColumn width="6">
                        <DateInput
                          name="startDate"
                          label="START DATE"
                          id="startDate"
                          direction="down"
                        />
                      </GridColumn>
                      <GridColumn width="6">
                        <DateInput
                          name="endDate"
                          label="END DATE"
                          id="endDate"
                          direction="down"
                        />
                      </GridColumn>
                    </GridRow>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={4}>
                        <Input
                          name="opportunity.locationCityStateCountry"
                          label="LOCATION"
                          disabled
                        />
                      </GridColumn>
                      <GridColumn width={4}>
                        <Input
                          name="opportunity.position.worksite"
                          label="SITE PREFERENCE"
                          disabled
                        />
                      </GridColumn>
                      <GridColumn width={4}>
                        <Dropdown
                          name="industryValue"
                          label="INDUSTRY"
                          options={industryOptions}
                          displayKey="label"
                          valueKey="value"
                        />
                      </GridColumn>
                    </GridRow>
                  </>
                ) : (
                  <>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={6}>
                        <DateInput
                          name="startDate"
                          label="START DATE"
                          id="startDate"
                        />
                      </GridColumn>
                      <GridColumn width={6}>
                        <DateInput
                          name="endDate"
                          label="END DATE"
                          id="endDate"
                          disabled={currentlyWorkHere}
                        />
                        <Gutter top="8px">
                          <Checkbox
                            name="currentlyWorkHere"
                            displayValue="I currently work here"
                            onChange={async checked => {
                              if (checked) {
                                await setValues({
                                  ...values,
                                  currentlyWorkHere: true,
                                  endDate: undefined,
                                });
                              }
                            }}
                          />
                        </Gutter>
                      </GridColumn>
                    </GridRow>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={12}>
                        <Dropdown
                          name="industryValue"
                          label="INDUSTRY"
                          options={industryOptions}
                          displayKey="label"
                          valueKey="value"
                        />
                      </GridColumn>
                    </GridRow>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={12}>
                        <Location
                          name="location"
                          label="LOCATION"
                          description="Enter the name of the relevant office location. If the exact address isn't found, enter the city and state."
                        />
                      </GridColumn>
                    </GridRow>
                  </>
                )}
                <GridRow>
                  <GridColumn width={12}>
                    <FormLabel name="DESCRIPTION_LABEL">DESCRIPTION</FormLabel>
                    <Gutter bottom="8px" />
                    <ReadonlyTextarea
                      value={description || ''}
                      formatting="modern"
                    />
                    <Gutter bottom="8px" />
                    <CondensedSmall>
                      Please update the description in Axiom for Talent
                    </CondensedSmall>
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                name="CANCEL_EXPERIENCE_BUTTON"
                onClick={onClose}
                variation="outline"
              >
                Cancel
              </Button>
              <Button name="SAVE_EXPERIENCE_BUTTON" onClick={fireSubmit}>
                Save Experience
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
